import { SESSION_STORAGE_COUNTRY_CODE } from '@/constants/session-storage';
import { CountryCodeEnum } from '@/enums/country-code-enum';

// External store do país do usuário, consumível via `useSyncExternalStore`. Existe porque o
// `sessionStorage` não emite eventos na mesma aba: sem este canal de notificação, quem lê o
// país (ex.: useCaptchaBypass) ficaria preso ao valor lido no mount, mesmo após o /user/getIp
// resolver. `setCountryCode` centraliza a escrita e avisa os assinantes.

type Listener = () => void;

const listeners = new Set<Listener>();

let storageListenerInstalled = false;

function notify(): void {
  listeners.forEach((listener) => listener());
}

// Reage a mudanças vindas de outras abas (o evento `storage` só dispara em documentos diferentes).
function ensureStorageListener(): void {
  if (storageListenerInstalled || typeof window === 'undefined') return;
  storageListenerInstalled = true;
  window.addEventListener('storage', (event: StorageEvent) => {
    if (event.key === SESSION_STORAGE_COUNTRY_CODE) notify();
  });
}

// Lê o país atual validando contra o enum. Fora do browser ou sem valor válido, retorna o
// default (US) — o mesmo fail-safe usado antes em getUserCountryCode.
export function readCountryCode(): CountryCodeEnum {
  if (typeof window === 'undefined' || !('sessionStorage' in window)) return CountryCodeEnum.UNITED_STATES;
  const stored = sessionStorage.getItem(SESSION_STORAGE_COUNTRY_CODE)?.toUpperCase();
  if (stored && Object.values(CountryCodeEnum).includes(stored as CountryCodeEnum)) return stored as CountryCodeEnum;
  return CountryCodeEnum.UNITED_STATES;
}

// Snapshot estável para SSR, evitando mismatch de hidratação.
export function getCountryCodeServerSnapshot(): CountryCodeEnum {
  return CountryCodeEnum.UNITED_STATES;
}

// Grava o país e notifica os assinantes da mesma aba.
export function setCountryCode(value: string): void {
  if (typeof window !== 'undefined' && 'sessionStorage' in window) sessionStorage.setItem(SESSION_STORAGE_COUNTRY_CODE, value);
  notify();
}

// Assina mudanças do país. Compatível com a assinatura esperada por `useSyncExternalStore`.
export function subscribeCountryCode(listener: Listener): () => void {
  ensureStorageListener();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
